import { useMemo } from 'react';
import { MdFileDownload } from 'react-icons/md';
import { AlexaVisibilityResult } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { Button } from '@Components/Button';
import { SeriesToggleLegend } from '@Components/Charts/TrendLineChart/SeriesToggleLegend';
import { TrendLineChart } from '@Components/Charts/TrendLineChart/TrendLineChart';
import { TrendSeries } from '@Components/Charts/TrendLineChart/trend-line-chart-types';
import { Appearance, Size } from '@Components/designConstants';
import { hexToRgb, mixColors, Neutral100, rgbToHex } from '@src/utils/colors';
import { PRODUCT_TREND_CHART_HEIGHT } from '../../alexa-visibility-constants';
import {
  BrandTopProducts,
  buildProductTrendExportRows,
  getProductTrendByDate
} from '../../alexa-visibility-trend-utils';
import { isSameBrand } from '../../alexa-visibility-utils';
import { useSeriesVisibility } from '../../hooks/useSeriesVisibility';
import { ExcelExportButton } from '../ExcelExportButton';

const PERCENT_TICKS = [0, 25, 50, 75, 100];
// How much each product's line is lightened toward white: the brand's own color, then two tints.
const PRODUCT_TINTS = [0, 0.4, 0.65];

const getProductColor = (brandColor: string, index: number): string =>
  rgbToHex(
    mixColors(
      hexToRgb(brandColor),
      hexToRgb(Neutral100),
      PRODUCT_TINTS[Math.min(index, PRODUCT_TINTS.length - 1)]
    )
  );

interface BrandProductsCardProps {
  group: BrandTopProducts;
  results: AlexaVisibilityResult[];
  brandColor: string;
  isTargetBrand: boolean;
}

const BrandProductsCard = ({
  group,
  results,
  brandColor,
  isTargetBrand
}: BrandProductsCardProps) => {
  const rows = useMemo(
    () => getProductTrendByDate(results, group.products),
    [group.products, results]
  );
  const series: TrendSeries[] = group.products.map((product, index) => ({
    key: product,
    label: product,
    color: getProductColor(brandColor, index)
  }));
  const keys = group.products;
  const { visibleKeys, toggle } = useSeriesVisibility(keys, group.products.length);

  return (
    <div className='aars-chart-card aars-product-trend-card'>
      <div className='aars-chart-card-header'>
        <div className='aars-chart-card-title'>
          <span className='aars-brand-chip' style={{ borderColor: brandColor }}>
            {group.brand}
          </span>
          {isTargetBrand && <span className='aars-target-brand-tag'>Target</span>}
        </div>
      </div>
      <SeriesToggleLegend series={series} visibleKeys={visibleKeys} onToggle={toggle} />
      <TrendLineChart
        rows={rows}
        series={series.filter(item => visibleKeys.includes(item.key))}
        height={PRODUCT_TREND_CHART_HEIGHT}
        yTicks={PERCENT_TICKS}
      />
    </div>
  );
};

interface ProductTrendsSectionProps {
  id: string;
  targetBrand: string;
  results: AlexaVisibilityResult[];
  groups: BrandTopProducts[];
  getBrandColor: (brand: string) => string;
  onDownloadPng: (id: string) => void;
}

// Each highlighted brand's three most recommended products, and how often each was recommended on
// every run date.
export const ProductTrendsSection = ({
  id,
  targetBrand,
  results,
  groups,
  getBrandColor,
  onDownloadPng
}: ProductTrendsSectionProps) => {
  const withProducts = groups.filter(group => group.products.length > 0);
  return (
    <div className='aars-product-trends' id={id}>
      <div className='aars-chart-card'>
        <div className='aars-chart-card-header'>
          <div className='aars-chart-card-title'>
            Top products by brand over time
            <span className='aars-chart-card-subtitle'>
              % of Alexa responses that recommend each of a brand&apos;s 3 most recommended
              products, per run date
            </span>
          </div>
          <div className='aars-comp-citation-actions'>
            <ExcelExportButton
              outlined
              label='Excel'
              errorTitle="Couldn't export the product trends"
              fileName={`AlexaVisibility_ProductTrends_${targetBrand}`}
              getSheets={() => [
                { name: 'Product Trends', data: buildProductTrendExportRows(results, withProducts) }
              ]}
            />
            <Button
              size={Size.SM}
              appearance={Appearance.OUTLINED}
              className='no-print'
              data-html2canvas-ignore='true'
              leadingElement={<MdFileDownload />}
              onClick={() => onDownloadPng(id)}
            >
              PNG
            </Button>
          </div>
        </div>
        {withProducts.length === 0 && (
          <div className='aars-trend-empty'>
            No recommended products could be matched to these brands.
          </div>
        )}
      </div>
      <div className='aars-product-trend-grid'>
        {withProducts.map(group => (
          <BrandProductsCard
            key={group.brand}
            group={group}
            results={results}
            brandColor={getBrandColor(group.brand)}
            isTargetBrand={isSameBrand(group.brand, targetBrand)}
          />
        ))}
      </div>
    </div>
  );
};
